import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { api, formatCHF } from "@/lib/api";
import { useNoIndex } from "@/lib/seo";
import { useSeo } from "@/lib/seo";

const longDate = (iso) => new Intl.DateTimeFormat("fr-CH", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(new Date(iso.length > 10 ? iso : `${iso}T12:00:00`));

const Solde = () => {
  useSeo({ title: "Règlement du solde · Entre Les Mondes", description: "", noindex: true });
  useNoIndex();
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [state, setState] = useState("loading");
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    document.title = "Régler mon solde — Entre les Mondes";
    api.solde(id).then((d) => { setB(d); setState("ok"); }).catch(() => setState("error"));
  }, [id]);

  const pay = async () => {
    setPaying(true);
    try {
      const r = await api.paySolde(id, window.location.origin);
      window.location.assign(r.checkout_url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de lancer le paiement, merci de réessayer.");
      setPaying(false);
    }
  };

  if (state === "loading") return <div className="pt-44 pb-32 text-center"><Loader2 className="w-8 h-8 text-terra mx-auto animate-spin" /></div>;
  if (state === "error") {
    return (
      <div className="pt-44 pb-32 text-center" data-testid="solde-notfound">
        <p className="font-serif text-3xl text-bark mb-6">Cette réservation est introuvable.</p>
        <Link to="/agenda" className="link-line text-terra">Retour à l'agenda</Link>
      </div>
    );
  }
  const regle = b.solde_statut === "réglé";
  const attente = b.statut === "en attente de paiement";
  const lieu = b.type === "lieu";
  const objet = lieu ? "visite" : "stage";

  return (
    <div className="pt-40 pb-24">
      <div className="max-w-2xl mx-auto px-6">
        <Reveal>
          <p className="eyebrow mb-4">Solde de votre {objet}</p>
          <h1 className="font-serif text-4xl lg:text-5xl text-bark leading-tight" data-testid="solde-title">
            {regle ? "Tout est réglé, merci." : attente ? "Acompte en attente" : `Bonjour ${b.nom.split(" ")[0]},`}
          </h1>
          <p className="mt-5 text-fumee leading-relaxed">
            {regle ? `Votre ${lieu ? "visite" : "place"} est définitivement confirmée. À très bientôt ${lieu ? "chez vous" : "à l'atelier"}.` : attente ? "L'acompte n'a pas encore été confirmé. Si vous venez de payer, patientez quelques instants puis rechargez cette page." : `Votre ${lieu ? "visite" : "place"} est bloquée grâce à votre acompte. Il reste ${formatCHF(b.solde_restant)} à régler au plus tard le jour ${lieu ? "de la visite" : "du stage"} (${longDate(b.solde_echeance)}), avant le début ${lieu ? "de la visite" : "de l'atelier"} — en ligne ci-dessous ou sur place.`}
          </p>
          <div className="mt-10 bg-card border border-sand-dark rounded-2xl p-8 space-y-3" data-testid="solde-recap">
            <p className="font-medium text-bark">{b.event_titre}</p>
            <p className="text-sm text-fumee capitalize">{longDate(b.event_date)}{b.date_individuelle ? ", 10 h – 18 h" : b.horaire ? `, ${b.horaire}` : ""}</p>
            <p className="text-sm text-fumee">{b.lieu} · réf. <span className="font-mono text-terra">{b.reference}</span></p>
            <div className="pt-3 border-t border-sand-dark text-sm space-y-1.5">
              <div className="flex justify-between text-fumee"><span>Total {lieu ? "de la visite" : "du stage"}</span><span>{formatCHF(b.montant)}</span></div>
              {b.remise_cadeau > 0 && <div className="flex justify-between text-fumee"><span>Carte cadeau</span><span>− {formatCHF(b.remise_cadeau)}</span></div>}
              <div className="flex justify-between text-fumee"><span>Acompte {attente ? "(en attente)" : "versé"}</span><span>− {formatCHF(b.acompte)}</span></div>
              <div className="flex justify-between items-baseline pt-2">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-fumee">{regle ? "Solde réglé" : "Reste à payer"}</span>
                <span className="font-serif text-2xl text-terra" data-testid="solde-montant">{formatCHF(regle ? 0 : b.solde_restant)}</span>
              </div>
            </div>
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            {regle ? (
              <span className="inline-flex items-center gap-2 text-sage"><CheckCircle2 className="w-5 h-5" /> Solde reçu</span>
            ) : !attente && (
              <button onClick={pay} disabled={paying} data-testid="solde-pay"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-terra text-earth text-sm hover:bg-earth transition-colors duration-300 disabled:opacity-60">
                {paying && <Loader2 className="w-4 h-4 animate-spin" />}
                {paying ? "Redirection…" : `Régler mon solde de ${formatCHF(b.solde_restant)} (TWINT / carte)`} <ArrowRight className="w-4 h-4" />
              </button>
            )}
            <Link to="/agenda" data-testid="solde-agenda" className="inline-flex items-center px-8 py-4 rounded-full border border-wood/30 text-bark text-sm hover:border-terra hover:text-terra transition-colors">Voir l'agenda</Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default Solde;
